import React, { FunctionComponent, useState, useEffect, useRef } from "react";
import { Select } from "antd";
import { DropdownComparisonWeight } from "../../../Enums/Enums";
import styled from "styled-components";
import { ChartButtonWrapper } from "../";
import {
    ResponsiveCalendar,
    ResponsiveCalendarCanvas,
    CalendarCommonProps,
} from "@nivo/calendar";
import { IChartCalendarDatapoint } from "../../../Models/Models";
import moment from "moment";
import { shortenStr } from "../../Common";
import { featureFlags } from "../../../Common/CurrentFeatureFlags";
import { RenderType } from "../../../Enums/RenderType";
import {
    CHILD_HEIGHT,
    isOutputSelected,
    registerElement,
    registerRef,
} from "../DigitalShare/DigitalShareGenerator";
import { DigitalShareAppType } from "../../../Enums/DigitalShareAppType";
import { DigitalShareItemType } from "../../../Enums/DigitalShareItemType";

const { Option } = Select;

interface INivoCalendar extends CalendarCommonProps {
  storeData: any[];
  benchmarkData?: any[];
  canvas?: boolean;
  displayDropdown?: boolean;
  sel1PrimaryName: string;
  sel1SecondaryName: string;
  sel2PrimaryType?: string;
  sel2SecondaryType?: string;
  sel2PrimaryName?: string;
  sel2SecondaryName?: string;
  sel1Color: string;
  sel2Color?: string;
  sel1Data: any;
  sel2Data: any;
  customGroupingDropdown?: Array<string>;
  customDropdownContent?: JSX.Element;
  location1Name: string;
  location2Name?: string;
  convertData: (data: any, type: string) => IChartCalendarDatapoint[];
  secondDropdownHandler?: (str: string) => void;
  renderType?: RenderType;
  app?: DigitalShareAppType;
  itemType?: DigitalShareItemType;
}

const ChartContainer = styled("div")`
  height: 360px;
  width: 100%;
`;

export const NivoCalendar: FunctionComponent<INivoCalendar> = (
    props: INivoCalendar
) => {
    const [currentData, setCurrentData] = useState(null);
    const [dropdownValue, setDropdownValue] = useState<number>(
        DropdownComparisonWeight.PRIMARY
    );
    const maxStrLen = 20;

    const renderType
    = props.renderType === undefined ? RenderType.DEFAULT : props.renderType;

    useEffect(() => {
        if (props.sel1Data && dropdownValue === DropdownComparisonWeight.PRIMARY) {
            setCurrentData(props.sel1Data);
        } else if (
            props.sel2Data
      && dropdownValue === DropdownComparisonWeight.SECONDARY
        ) {
            setCurrentData(props.sel2Data);
        }
    }, [props.sel1Data]);

    const calendarProps: CalendarCommonProps = currentData && {
        emptyColor: "#eeeeee",
        colors: [
            `${props.sel1Color}25`,
            `${props.sel1Color}50`,
            `${props.sel1Color}75`,
            props.sel1Color,
        ],
        margin: { left: 20, top: 5 },
        direction: "horizontal",
        monthBorderColor: "#0a0a0a",
        dayBorderWidth: 1,
        dayBorderColor: "#ffffff",
        tooltip: ({ day, value }) => (
            <strong>
                {moment(day).format("dddd, MMMM Do YYYY")}: {`${value}%`}
            </strong>
        ),
    };

    const handleVisitsChange = (value: DropdownComparisonWeight) => {
        switch (value) {
            case DropdownComparisonWeight.PRIMARY:
                setCurrentData(props.sel1Data);
                break;
            case DropdownComparisonWeight.SECONDARY:
                setCurrentData(props.sel2Data);
                break;
        }
        setDropdownValue(value);
    };

    const calcFromDate = (dateStr: string): string => {
        return `${dateStr.substring(0, 4)}-01-02`;
    };

    const calcToDate = (dateStr: string): string => {
        return `${dateStr.substring(0, 4)}-12-31`;
    };

    const CalendarType: JSX.Element = currentData ? (
        props.canvas ? (
            <ResponsiveCalendarCanvas
                from={calcFromDate(currentData[0].day)}
                to={calcToDate(currentData[currentData.length - 1].day)}
                {...calendarProps}
                data={currentData}
            />
        ) : (
            <ResponsiveCalendar
                from={calcFromDate(currentData[0].day)}
                to={calcToDate(currentData[currentData.length - 1].day)}
                {...calendarProps}
                data={currentData}
            />
        )
    ) : null;

    const InteractionArea: JSX.Element = (
        <ChartButtonWrapper>
            <div>
                {props.displayDropdown ? (
                    <Select
                        style={{ marginRight: "10px" }}
                        defaultValue={DropdownComparisonWeight.PRIMARY}
                        value={dropdownValue}
                        onChange={handleVisitsChange}
                    >
                        <Option value={DropdownComparisonWeight.PRIMARY}>
                            {shortenStr(props.sel1PrimaryName, maxStrLen)}
                        </Option>
                        <Option value={DropdownComparisonWeight.SECONDARY}>
                            {shortenStr(props.sel1SecondaryName, maxStrLen)}
                        </Option>
                    </Select>
                ) : null}
                {props.customDropdownContent}
            </div>
            <div></div>
        </ChartButtonWrapper>
    );

    const ref = useRef();

    const getChartText = (visits: any, selectedPolygon?: string): string => {
        return `${visits ? "Visits" : "Visitors"} volume trends${
            selectedPolygon.length && "of" + selectedPolygon
        }`;
    };

    if (
        featureFlags.DigitalShare
    && renderType === RenderType.DIGITAL_SHARE
    && isOutputSelected(props.app, props.itemType)
    ) {
        const calendar = (
            <ChartContainer
                style={{
                    padding: "0 16px",
                    height: props.renderType == RenderType.DIGITAL_SHARE && CHILD_HEIGHT,
                }}
            >
                {CalendarType}
            </ChartContainer>
        );

        const text = getChartText(dropdownValue);

        registerRef(props.app.toString(), props.itemType.toString(), ref);
        registerElement(
            props.app.toString(),
            props.itemType.toString(),
            text,
            calendar
        );
    }

    return (
        <>
            {InteractionArea}
            <ChartContainer style={{ padding: "0 16px" }}>
                {currentData && CalendarType}
            </ChartContainer>
        </>
    );
};
